/**
 * One-off dated class schedule slots (#3354).
 *
 * A `class_schedules` row with `effective_date = end_date = <one date>` is a
 * slot that meets exactly ONCE — the mechanism behind "we're closed Sunday
 * 9/27, so that week's class runs Saturday 9/26 instead". No new column or
 * table: every expansion surface (calendar, drop-in booking, proration,
 * attendance) already honors a schedule row's effective/end window, so a
 * one-day window simply fires on that one date.
 *
 * This module is the ONE definition of "is this slot a one-off dated slot"
 * plus the helpers the class form, the class APIs, and the read-only detail
 * surfaces share. All values are date-only YYYY-MM-DD strings; nothing here
 * ever constructs `new Date('<date-only>')` (UTC-midnight footgun).
 */

import { YYYY_MM_DD_REGEX } from '@/utils/date/calendarDate';

/** A slot-ish record carrying an optional per-row date window. */
export interface SlotWithDateWindow {
  effective_date?: string | null;
  end_date?: string | null;
}

/** An "added class date" as the class form models it: one extra date a class
 *  meets, with its own times. Persisted as a one-off `class_schedules` row. */
export interface AddedClassDate {
  date: string; // YYYY-MM-DD
  start_time: string; // HH:mm or HH:mm:ss
  end_time: string;
  instructor_ids?: string[];
  location_id?: string;
}

/** Normalizes a wire value ('2026-09-26' or an ISO timestamp) to a date-only
 *  string, or null when it isn't one. */
export function toDateOnly(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const sliced = value.slice(0, 10);
  return YYYY_MM_DD_REGEX.test(sliced) ? sliced : null;
}

/**
 * True when the slot carries a one-day window (effective_date = end_date =
 * one valid date) — i.e. it is an added/moved class date, not a weekly slot.
 */
export function isOneOffDatedSlot(slot: SlotWithDateWindow | null | undefined): boolean {
  if (!slot) return false;
  const start = toDateOnly(slot.effective_date);
  const end = toDateOnly(slot.end_date);
  return start !== null && end !== null && start === end;
}

/** The class-level session window, for telling a one-day session class's
 *  ordinary slots apart from genuinely added dates. */
export interface ClassSessionWindow {
  session_start_date?: string | null;
  session_end_date?: string | null;
}

/**
 * True when the slot is a genuinely ADDED one-off date. One carve-out beyond
 * `isOneOffDatedSlot`: a ONE-DAY session class (a single-day clinic where
 * session start = session end) stamps every ordinary weekly slot with that
 * same one-day window — those are the class's normal schedule, not added
 * dates, so they must stay in the weekly editor and carry no "added" label.
 */
export function isAddedDateSlot(
  slot: SlotWithDateWindow | null | undefined,
  classWindow?: ClassSessionWindow | null,
): boolean {
  if (!isOneOffDatedSlot(slot)) return false;
  const date = toDateOnly(slot?.effective_date);
  const winStart = toDateOnly(classWindow?.session_start_date);
  const winEnd = toDateOnly(classWindow?.session_end_date);
  if (winStart && winStart === winEnd && date === winStart) return false;
  return true;
}

/**
 * The weekday (0=Sunday … 6=Saturday) a date-only string falls on. Parsed at
 * midday UTC so the answer never shifts with the runtime timezone — the same
 * idiom `validateDropInScheduleDates` uses.
 */
export function dayOfWeekFromDateOnly(date: string): number {
  return new Date(`${date}T12:00:00.000Z`).getUTCDay();
}

/**
 * A slot-supplied date window the class save routes may persist verbatim.
 * ONE-DAY windows only — that is the entire added-date feature, and it is
 * also the safety boundary: accepting arbitrary multi-day windows would let a
 * stale client (whose JS spreads `...slot` and echoes back the windows GET
 * now returns) freeze weekly rows' windows on save. Anything that is not a
 * valid one-day window returns null, and the caller falls back to its legacy
 * class-level stamping.
 */
export function readSlotDateWindow(
  slot: SlotWithDateWindow | null | undefined,
): { effective_date: string; end_date: string } | null {
  if (!slot) return null;
  const start = toDateOnly(slot.effective_date);
  const end = toDateOnly(slot.end_date);
  if (!start || !end || end !== start) return null;
  return { effective_date: start, end_date: end };
}

/** Splits a section's schedule rows into weekly slots and ADDED one-off dates
 *  (see `isAddedDateSlot` for the one-day-session-class carve-out). */
export function partitionScheduleSlots<S extends SlotWithDateWindow>(
  schedule: S[] | null | undefined,
  classWindow?: ClassSessionWindow | null,
): { weeklySlots: S[]; oneOffSlots: S[] } {
  const weeklySlots: S[] = [];
  const oneOffSlots: S[] = [];
  for (const slot of schedule || []) {
    (isAddedDateSlot(slot, classWindow) ? oneOffSlots : weeklySlots).push(slot);
  }
  return { weeklySlots, oneOffSlots };
}

/**
 * Builds the schedule-slot payload for one added date: a one-day window on the
 * date's own weekday. The class API persists it as a `class_schedules` row.
 */
export function oneOffSlotFromAddedDate(added: AddedClassDate): {
  day_of_week: number;
  start_time: string;
  end_time: string;
  effective_date: string;
  end_date: string;
  instructor_ids: string[];
  location_id?: string;
} {
  return {
    day_of_week: dayOfWeekFromDateOnly(added.date),
    start_time: added.start_time,
    end_time: added.end_time,
    effective_date: added.date,
    end_date: added.date,
    instructor_ids: added.instructor_ids || [],
    location_id: added.location_id,
  };
}

/** The inverse mapping: a persisted one-off slot back to the form's model. */
export function addedDateFromOneOffSlot(slot: SlotWithDateWindow & {
  start_time: string;
  end_time: string;
  instructor_ids?: string[];
  location_id?: string;
}): AddedClassDate | null {
  const date = toDateOnly(slot.effective_date);
  if (!date || !isOneOffDatedSlot(slot)) return null;
  return {
    date,
    start_time: slot.start_time,
    end_time: slot.end_time,
    instructor_ids: slot.instructor_ids,
    location_id: slot.location_id,
  };
}
